import { useState, useEffect, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";

/* ── Icons ──────────────────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconCheck    = () => <Icon d="M20 6 9 17 4 12" stroke={2.5} />;
const IconX        = () => <Icon d="M18 6 6 18M6 6l12 12" size={18} stroke={2} />;
const IconClock    = () => <Icon d={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z","M12 6v6l4 2"]} />;
const IconCash     = () => <Icon size={20} d={["M12 1v22","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"]} />;
const IconUser     = () => <Icon d={["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2","M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]} />;
const IconRefresh  = () => <Icon d={["M1 4v6h6","M3.51 15a9 9 0 1 0 2.13-9.36L1 10"]} />;
const IconInbox    = () => <Icon size={36} d={["M22 12h-6l-2 3H10l-2-3H2","M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"]} />;

const fmt = (n) => `${Number(n || 0).toFixed(3)} TND`;

/* ── Scoped CSS (unchanged) ────────────────────────────── */
const PAGE_CSS = `
  .wa-page {
    min-height: 100vh;
    background: #0d0f18;
    color: #e2e4f0;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-size: 13px;
    padding: 32px 36px;
  }
  .wa-page__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
  }
  .wa-page__title {
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .wa-page__title svg {
    color: #ffa800;
  }
  .wa-page__actions {
    display: flex;
    gap: 12px;
  }

  /* Stats row */
  .wa-stats {
    display: flex;
    gap: 16px;
    margin-bottom: 28px;
  }
  .wa-stat-card {
    background: #111420;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 18px 22px;
    min-width: 160px;
    transition: border-color 0.2s;
  }
  .wa-stat-card:hover {
    border-color: rgba(99,102,241,0.3);
  }
  .wa-stat-card__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: rgba(255,255,255,0.4);
    margin-bottom: 8px;
    font-weight: 600;
  }
  .wa-stat-card__value {
    font-size: 24px;
    font-weight: 700;
    color: #8b8ff7;
  }

  /* Card list */
  .wa-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .wa-card {
    background: #111420;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 18px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: border-color 0.2s, background 0.2s;
  }
  .wa-card:hover {
    border-color: rgba(99,102,241,0.25);
    background: #161a2a;
  }
  .wa-card__left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .wa-card__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(255,168,0,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffa800;
  }
  .wa-card__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .wa-card__amount {
    font-size: 16px;
    font-weight: 700;
    color: #fff;
  }
  .wa-card__meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: rgba(255,255,255,0.45);
  }
  .wa-card__meta svg {
    flex-shrink: 0;
    color: rgba(255,255,255,0.3);
  }
  .wa-card__reason {
    font-size: 12px;
    color: rgba(255,255,255,0.5);
    font-style: italic;
    max-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }
  .wa-card__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  /* Buttons */
  .wa-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .wa-btn--approve {
    background: linear-gradient(135deg, #2ed573, #26a65b);
    color: #fff;
    box-shadow: 0 4px 12px rgba(46,213,115,0.3);
  }
  .wa-btn--approve:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(46,213,115,0.45);
  }
  .wa-btn--reject {
    background: rgba(255,71,87,0.1);
    border: 1px solid rgba(255,71,87,0.3);
    color: #ff6b7a;
  }
  .wa-btn--reject:hover {
    background: rgba(255,71,87,0.2);
    color: #fff;
  }
  .wa-btn--refresh {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.6);
  }
  .wa-btn--refresh:hover {
    border-color: #6366f1;
    color: #8b8ff7;
    background: rgba(99,102,241,0.08);
  }
  .wa-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  /* Empty state */
  .wa-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 0;
    color: rgba(255,255,255,0.2);
  }
  .wa-empty svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }
  .wa-empty p {
    font-size: 14px;
    color: rgba(255,255,255,0.35);
  }

  /* Rejection Modal */
  .wa-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 300;
    backdrop-filter: blur(4px);
  }
  .wa-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 16px;
    width: 400px;
    max-width: 95vw;
    box-shadow: 0 24px 60px rgba(0,0,0,0.6);
    padding: 28px;
  }
  .wa-modal h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .wa-modal textarea {
    width: 100%;
    padding: 10px 14px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    color: #e2e4f0;
    font-size: 13px;
    resize: vertical;
    min-height: 80px;
    outline: none;
    font-family: inherit;
  }
  .wa-modal textarea:focus {
    border-color: #6366f1;
  }
  .wa-modal__actions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
  }
  .wa-modal__cancel,
  .wa-modal__confirm {
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .wa-modal__cancel {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.5);
  }
  .wa-modal__cancel:hover {
    border-color: rgba(255,255,255,0.2);
    color: #fff;
  }
  .wa-modal__confirm {
    background: linear-gradient(135deg, #ff4757, #e84118);
    color: #fff;
    border: none;
  }
  .wa-modal__confirm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function WithdrawalApprovalPage() {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  // Rejection modal state
  const [rejectModal, setRejectModal] = useState({ open: false, withdrawalId: null });
  const [rejectionReason, setRejectionReason] = useState("");

  // Inject CSS
  useEffect(() => {
    const styleId = "wa-page-css";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent = PAGE_CSS;
      document.head.appendChild(el);
    }
  }, []);

  const branchId = user?.branch?.id;

  const fetchWithdrawals = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const res = await axiosPrivate.get(`/api/withdrawals/branch/${branchId}/pending`);
      setWithdrawals(res.data);
    } catch (err) {
      toast.error("Failed to load withdrawals");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, branchId]);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);

  // Updated to send { approve: true/false } per backend DTO
  const handleDecide = async (withdrawalId, approve, reason = "") => {
    setProcessing(withdrawalId);
    try {
      const payload = {
        approve: approve,
        ...(approve === false && { rejectionReason: reason }),
      };
      await axiosPrivate.post(`/api/withdrawals/${withdrawalId}/decide`, payload);
      toast.success(`Withdrawal ${approve ? "approved" : "rejected"}`);
      setWithdrawals(prev => prev.filter(w => w.id !== withdrawalId));
    } catch (err) {
      toast.error(err.response?.data?.message || "Decision failed");
    } finally {
      setProcessing(null);
    }
  };

  const handleApprove = (id) => handleDecide(id, true);

  // Open rejection modal
  const openRejectModal = (id) => {
    setRejectModal({ open: true, withdrawalId: id });
    setRejectionReason("");
  };

  const confirmReject = () => {
    if (rejectModal.withdrawalId) {
      handleDecide(rejectModal.withdrawalId, false, rejectionReason);
      setRejectModal({ open: false, withdrawalId: null });
    }
  };

  const totalPending = withdrawals.length;
  const totalAmount = withdrawals.reduce((sum, w) => sum + Number(w.amount || 0), 0);

  return (
    <div className="wa-page mt-4">
      <div className="wa-page__header">
        <div className="wa-page__title">
          <IconCash /> Withdrawal Approvals
        </div>
        <div className="wa-page__actions">
          <button className="wa-btn wa-btn--refresh" onClick={fetchWithdrawals} disabled={loading}>
            <IconRefresh /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="wa-stats">
        <div className="wa-stat-card">
          <div className="wa-stat-card__label">Pending Requests</div>
          <div className="wa-stat-card__value">{totalPending}</div>
        </div>
        <div className="wa-stat-card">
          <div className="wa-stat-card__label">Total Value</div>
          <div className="wa-stat-card__value">{fmt(totalAmount)}</div>
        </div>
      </div>

      {loading ? (
        <div className="wa-empty">
          <p>Loading...</p>
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="wa-empty">
          <IconInbox />
          <p>All clear — no pending withdrawals.</p>
        </div>
      ) : (
        <div className="wa-list">
          {withdrawals.map(w => (
            <div key={w.id} className="wa-card">
              <div className="wa-card__left">
                <div className="wa-card__icon">
                  <IconCash />
                </div>
                <div className="wa-card__info">
                  <div className="wa-card__amount">{fmt(w.amount)}</div>
                  <div className="wa-card__meta">
                    <IconUser size={14} />
                    <span>{w.requestedByName || "Unknown"}</span>
                    <IconClock size={14} />
                    <span>{new Date(w.createdAt).toLocaleDateString()} {new Date(w.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  {w.reason && (
                    <div className="wa-card__reason" title={w.reason}>
                      {w.reason}
                    </div>
                  )}
                </div>
              </div>
              <div className="wa-card__actions">
                <button
                  className="wa-btn wa-btn--approve"
                  disabled={processing === w.id}
                  onClick={() => handleApprove(w.id)}
                >
                  <IconCheck /> Approve
                </button>
                <button
                  className="wa-btn wa-btn--reject"
                  disabled={processing === w.id}
                  onClick={() => openRejectModal(w.id)}
                >
                  <IconX /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectModal.open && (
        <div className="wa-modal-overlay" onClick={() => setRejectModal({ open: false, withdrawalId: null })}>
          <div className="wa-modal" onClick={e => e.stopPropagation()}>
            <h3><IconX /> Reject Withdrawal</h3>
            <textarea
              placeholder="Enter reason for rejection (optional)..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              autoFocus
            />
            <div className="wa-modal__actions">
              <button className="wa-modal__cancel" onClick={() => setRejectModal({ open: false, withdrawalId: null })}>
                Cancel
              </button>
              <button className="wa-modal__confirm" onClick={confirmReject}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}